/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Checkbox, FormGroup, FormControlLabel } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import type { LayerFoundation } from '@opengeoweb/webmap';
import { LayerType } from '@opengeoweb/webmap';
import { textStyleWithMargin } from './LayerInfoText';
import { useWebmapReactTranslation } from '../../utils/i18n';
import { LegendLayout } from '../Legend';
import { StacLegendCanvas } from '../Legend/StacLegendCanvas';

export interface LayerInfoLegendProps {
  title: string;
  legendURL?: string;
  showLayerLegend: boolean;
  onToggleLayerLegendVisibility?: (showLegend: boolean) => void;
  layerType?: LayerType;
  stacLayer?: Pick<LayerFoundation, 'service' | 'id' | 'style'>;
  layerOpacity?: number;
}

export const LayerInfoLegend: React.FC<LayerInfoLegendProps> = ({
  title,
  legendURL,
  showLayerLegend,
  onToggleLayerLegendVisibility,
  layerType,
  stacLayer,
  layerOpacity,
}: LayerInfoLegendProps) => {
  const { t } = useWebmapReactTranslation();
  const [legendHeight, setLegendHeight] = useState(180);
  const [legendWidth, setLegendWidth] = useState(150);
  const isStacLegend = layerType === LayerType.stacGeoTIFFLayer && !!stacLayer;
  let legendContent = legendURL ? <img alt={title} src={legendURL} /> : '-';
  if (isStacLegend && stacLayer) {
    legendContent = (
      <StacLegendCanvas
        height={legendHeight}
        layer={stacLayer}
        onHeightChange={setLegendHeight}
        onWidthChange={setLegendWidth}
        opacity={layerOpacity ?? 1}
        width={legendWidth}
      />
    );
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography sx={textStyleWithMargin} variant="subtitle2">
          {t('webmap-react-legend')}
        </Typography>
        {!!onToggleLayerLegendVisibility && (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showLayerLegend}
                  onChange={() => {
                    onToggleLayerLegendVisibility(!showLayerLegend);
                  }}
                />
              }
              label={t('webmap-react-show-layer-legend')}
              sx={textStyleWithMargin}
            />
          </FormGroup>
        )}
      </Box>
      <LegendLayout
        height={isStacLegend ? legendHeight : undefined}
        title={title}
        width={isStacLegend ? legendWidth : undefined}
      >
        {legendContent}
      </LegendLayout>
    </>
  );
};
